import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import TextAlign from "@tiptap/extension-text-align";
import Color from "@tiptap/extension-color";
import Underline from "@tiptap/extension-underline";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import DOMPurify from "dompurify";

interface RichTextAreaProps {
  key?: string;
  label?: string;
  name?: string;
  placeholder?: string;
  value?: string; // HTML string
  onChange?: (html: string) => void;
  error?: boolean;
  helperText?: string;
  readOnly?: boolean;
  minHeight?: string | number;
}

const FONT_OPTIONS = [
  { label: "Default", value: "" },
  { label: "Arial", value: "Arial, Helvetica, sans-serif" },
  { label: "Times New Roman", value: "'Times New Roman', Times, serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Courier New", value: "'Courier New', Courier, monospace" },
];

const SIZE_OPTIONS = [
  { label: "12", value: "12px" },
  { label: "14", value: "14px" },
  { label: "16", value: "16px" },
  { label: "18", value: "18px" },
  { label: "24", value: "24px" },
  { label: "32", value: "32px" },
];

const RichTextArea: React.FC<RichTextAreaProps> = ({
  key = "",
  label,
  name,
  placeholder,
  value = "",
  onChange,
  error,
  helperText,
  readOnly = false,
  minHeight = 200,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Color,
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Image,
      Link.configure({ openOnClick: false }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const sanitized = DOMPurify.sanitize(html);
      onChange && onChange(sanitized);
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (value && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const setFont = (font: string) => {
    if (!editor) return;
    const style = font ? `font-family: ${font};` : "";
    editor.chain().focus().setMark("textStyle", { style }).run();
  };

  const setSize = (size: string) => {
    if (!editor) return;
    const style = size ? `font-size: ${size};` : "";
    editor.chain().focus().setMark("textStyle", { style }).run();
  };

  const insertImage = () => {
    if (!editor) return;
    const url = window.prompt("Image URL");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  const addLink = () => {
    if (!editor) return;
    const url = window.prompt("URL");
    if (url)
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url })
        .run();
  };

  return (
    <div key={key} className="flex flex-col items-start space-y-2">
      {label && (
        <label className="text-sm font-medium text-gray-700">{label}</label>
      )}

      <div
        className={`w-full border ${
          error ? "border-red-400" : "border-gray-300"
        } rounded-lg p-2`}
      >
        {/* Toolbar */}
        <div className="flex flex-wrap gap-2 mb-2 items-center">
          <select
            className="border rounded px-2 py-1 text-sm"
            onChange={(e) => setFont(e.target.value)}
            defaultValue={""}
          >
            {FONT_OPTIONS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>

          <select
            className="border rounded px-2 py-1 text-sm"
            onChange={(e) => setSize(e.target.value)}
            defaultValue={"16px"}
          >
            {SIZE_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>

          <div className="flex gap-1">
            <button
              type="button"
              className={`px-2 py-1 rounded ${
                editor?.isActive("bold") ? "bg-gray-200" : ""
              }`}
              onClick={() => editor?.chain().focus().toggleBold().run()}
            >
              B
            </button>
            <button
              type="button"
              className={`px-2 py-1 rounded ${
                editor?.isActive("italic") ? "bg-gray-200" : ""
              }`}
              onClick={() => editor?.chain().focus().toggleItalic().run()}
            >
              I
            </button>
            <button
              type="button"
              className={`px-2 py-1 rounded ${
                editor?.isActive("underline") ? "bg-gray-200" : ""
              }`}
              onClick={() => editor?.chain().focus().toggleUnderline().run()}
            >
              U
            </button>
            <button
              type="button"
              className={`px-2 py-1 rounded ${
                editor?.isActive("strike") ? "bg-gray-200" : ""
              }`}
              onClick={() => editor?.chain().focus().toggleStrike().run()}
            >
              S
            </button>
          </div>

          <div className="flex gap-1">
            <button
              type="button"
              className={`px-2 py-1 rounded ${
                editor?.isActive({ textAlign: "left" }) ? "bg-gray-200" : ""
              }`}
              onClick={() => editor?.chain().focus().setTextAlign("left").run()}
            >
              L
            </button>
            <button
              type="button"
              className={`px-2 py-1 rounded ${
                editor?.isActive({ textAlign: "center" }) ? "bg-gray-200" : ""
              }`}
              onClick={() =>
                editor?.chain().focus().setTextAlign("center").run()
              }
            >
              C
            </button>
            <button
              type="button"
              className={`px-2 py-1 rounded ${
                editor?.isActive({ textAlign: "right" }) ? "bg-gray-200" : ""
              }`}
              onClick={() =>
                editor?.chain().focus().setTextAlign("right").run()
              }
            >
              R
            </button>
            <button
              type="button"
              className={`px-2 py-1 rounded ${
                editor?.isActive({ textAlign: "justify" }) ? "bg-gray-200" : ""
              }`}
              onClick={() =>
                editor?.chain().focus().setTextAlign("justify").run()
              }
            >
              J
            </button>
          </div>

          <div className="flex gap-1">
            <button
              type="button"
              className={`px-2 py-1 rounded ${
                editor?.isActive("bulletList") ? "bg-gray-200" : ""
              }`}
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
            >
              •
            </button>
            <button
              type="button"
              className={`px-2 py-1 rounded ${
                editor?.isActive("orderedList") ? "bg-gray-200" : ""
              }`}
              onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            >
              1.
            </button>
          </div>

          <div className="flex gap-1">
            <button
              type="button"
              className="px-2 py-1 rounded"
              onClick={insertImage}
            >
              Img
            </button>
            <button
              type="button"
              className="px-2 py-1 rounded"
              onClick={addLink}
            >
              Link
            </button>
            <button
              type="button"
              className="px-2 py-1 rounded"
              onClick={() =>
                editor?.chain().focus().clearNodes().unsetAllMarks().run()
              }
            >
              Clean
            </button>
          </div>
        </div>

        {/* Editor */}
        <div
          className="richtext-editor prose max-w-full"
          style={{
            minHeight:
              typeof minHeight === "number" ? `${minHeight}px` : minHeight,
          }}
        >
          <EditorContent editor={editor} />
        </div>
      </div>

      {helperText && (
        <span className={`text-xs ${error ? "text-red-500" : "text-gray-500"}`}>
          {helperText}
        </span>
      )}
    </div>
  );
};

export default RichTextArea;
